import React from "react";
import { connect } from "react-redux";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import { ReportView } from "model/Report";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { useFlags } from "launchdarkly-react-client-sdk";
import IconReportsTrend from "lib/images/icon-reports-trend.svg";

const StyledNav = styled.nav`
  background-color: #0e3f62;
  width: 250px;
  flex-shrink: 0;
`;

const Heading = styled.h3`
  color: white;
  font-family: Inter, system-ui;
  font-weight: 500;
  padding: 18px;
  margin: 0;

  & img {
    width: 1.2em;
    vertical-align: middle;
    margin-right: 0.3em;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 8px 18px 18px 18px;
  margin: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.15);

  & li {
    width: 100%;

    & a {
      position: relative;
      font-family: Inter, system-ui;
      font-size: 14px;
      font-weight: normal;
      color: white;
      display: inline-block;
      padding: 0.5em 0;

      &::after {
        content: "";
        display: block;
        width: 100%;
        height: 1px;
        background: white;
      }

      &:not(.active) {
        opacity: 0.8;
        transition: opacity 0.15s ease;

        &::after {
          background: transparent;
        }

        @media (hover: hover) {
          &:hover {
            opacity: 1;
          }
        }
      }
    }
  }
`;

interface Authentication {
  isSystemAdmin: boolean;
  user: { id: number } | null;
}

interface ReportNavigationProps {
  activeReportId: string | null;
  availableReportList: ReportView[] | null;
  changeReportsOrder: (indexFrom: number, indexTo: number) => void;
  authentication?: Authentication;
}

const ReportNavigation = (props: ReportNavigationProps) => {
  const {
    activeReportId,
    availableReportList,
    changeReportsOrder,
    authentication,
  } = props;
  const { zep5ShowSelfServiceReports: ssrEnabled } = useFlags();
  const showSSRLink = ssrEnabled || authentication?.isSystemAdmin;
  const reportTypeListItems =
    availableReportList && availableReportList.length
      ? availableReportList.map((value, index) => {
          const reportName = value.name;
          const reportId = value.reportId;
          return (
            <Draggable key={reportId} draggableId={reportId} index={index}>
              {(provided) => (
                <li
                  ref={provided.innerRef}
                  {...provided.dragHandleProps}
                  {...provided.draggableProps}
                >
                  <NavLink
                    to={`/reporting?reportId=${reportId}`}
                    className={
                      activeReportId === reportId ? "active" : undefined
                    }
                  >
                    {reportName}
                  </NavLink>
                </li>
              )}
            </Draggable>
          );
        })
      : [];

  const handleDrop = (reportItem: DropResult) => {
    if (!reportItem.destination) {
      return;
    }
    changeReportsOrder(reportItem.source.index, reportItem.destination.index);
  };

  return (
    <StyledNav>
      <Heading>
        <img src={IconReportsTrend} /> Report Navigation
      </Heading>
      {reportTypeListItems.length > 0 ? (
        <>
          <DragDropContext onDragEnd={handleDrop}>
            <Droppable droppableId="report-list">
              {(provided) => (
                <List {...provided.droppableProps} ref={provided.innerRef}>
                  {reportTypeListItems}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
          {showSSRLink && (
            <List>
              <li>
                <NavLink
                  to={`/reporting/self-service-reports`}
                  className={(isActive) => (isActive ? "active" : "")}
                >
                  Self Service Reports
                </NavLink>
              </li>
            </List>
          )}
        </>
      ) : null}
    </StyledNav>
  );
};

// @ts-ignore
const mapStateToProps = ({ authentication }) => ({ authentication });

export default connect(mapStateToProps, null)(ReportNavigation);
